import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { mmToPx, PAPER_PRESETS, paperById, paperDimsMm } from "../export/paper.js";
import type {
  AssemblyInfo,
  ExportFormat,
  ExportRequest,
  PageDef,
  PaperOrientation,
  PaperSize,
} from "../export/types.js";

export interface ExportMode {
  /** Stable id passed back into {@link ExportDialogProps.buildPages}. */
  id: string;
  /** Short label shown in the mode toggle. */
  label: string;
  /** When true, the scale slider + multi-page tile preview is shown. */
  supportsScale?: boolean;
  /**
   * Paper ids offered as one-click chips in this mode's **simple view**.
   *
   * Setting this turns the mode into a poster-style mode: it opens on a
   * stripped-down view that asks for nothing but the sheet size and hides
   * every other control behind an "Erweiterte Optionen" link. Leave it unset
   * for modes that should always show the full option list.
   */
  simplePaperIds?: readonly string[];
  /** Paper preselected when entering the simple view. Defaults to the first chip. */
  simpleDefaultPaperId?: string;
  /**
   * Sheet a tiled poster is actually printed on (A4 in practice). Setting it
   * enables the "print across several sheets" switch whenever the chosen
   * poster size differs from this sheet.
   */
  printSheetId?: string;
  /** Glue margin used when tiling, in millimetres. Defaults to 5. */
  posterOverlapMm?: number;
}

export interface ExportLayoutPreview {
  /** Tile grid the buildPages will produce at the current settings. */
  cols: number;
  rows: number;
  /** Board width in board units (logical). */
  boardW: number;
  /** Board height in board units. */
  boardH: number;
  /** One paper page's coverage in board units (same units as boardW/H). */
  pageBoardW: number;
  pageBoardH: number;
  /**
   * Board-units advanced between adjacent tile origins (= page − overlap).
   * Defaults to the page size (no overlap) when omitted.
   */
  stepBoardW?: number;
  stepBoardH?: number;
  /** Physical overlap between tiles in mm, for the caption. */
  overlapMm?: number;
}

export interface ExportDialogBuildArgs {
  paper: PaperSize;
  orientation: PaperOrientation;
  modeId: string;
  /** 1.0 = fit-to-one-page; >1 tiles across more pages. */
  scale: number;
  /** Physical overlap in mm shared between neighbouring tiles at their seams. */
  overlapMm: number;
  /**
   * Print the poster across smaller sheets that get taped together, instead
   * of onto a single sheet of `paper`. The surface decides which sheet to
   * print on and how to lay out the grid.
   */
  tileOntoPrintSheet: boolean;
}

export interface ExportDialogProps {
  title?: string;
  /**
   * Build the list of pages once the user picks paper + orientation + mode.
   * The dialog passes the current settings in; the surface decides how to
   * paginate (frames-as-pages, current-frame-with-scale, viewport tiles, …).
   */
  buildPages: (args: ExportDialogBuildArgs) => PageDef[];
  /**
   * Optional: describe the tile grid for the mini layout preview. Return
   * `null` to suppress the preview (e.g. for frames mode where every page
   * is a discrete frame, not a tile).
   */
  getLayoutPreview?: (args: ExportDialogBuildArgs) => ExportLayoutPreview | null;
  /**
   * Optional: describe the multi-sheet poster the current settings produce, so
   * the PDF can carry an assembly guide. Return `null` when the export is not
   * a tiled poster.
   */
  getAssembly?: (args: ExportDialogBuildArgs) => AssemblyInfo | null;
  /**
   * Optional list of export modes. When provided, a mode-toggle is shown
   * at the top of the dialog. When omitted, a single implicit "default"
   * mode is used (modeId = "default").
   */
  modes?: ReadonlyArray<ExportMode>;
  defaultModeId?: string;
  /** Default filename without extension. */
  defaultFilename: string;
  /** Default paper preset id. Defaults to "A4". */
  defaultPaperId?: string;
  /** Default orientation. Defaults to "landscape". */
  defaultOrientation?: PaperOrientation;
  /** Default export format. Defaults to "pdf". */
  defaultFormat?: ExportFormat;
  /** Fired with the assembled request when the user confirms. */
  onConfirm: (request: ExportRequest) => Promise<void> | void;
  onClose: () => void;
}

const SCALE_PRESETS = [
  { label: "Fit", value: 1 },
  { label: "150%", value: 1.5 },
  { label: "200%", value: 2 },
  { label: "300%", value: 3 },
  { label: "400%", value: 4 },
];

/** Overlap presets in mm shown as quick-select chips. */
const OVERLAP_PRESETS = [0, 5, 10, 15, 20];

const DEFAULT_MODE: ExportMode = { id: "default", label: "Standard" };

/** Paper a mode's simple view should start on, or null if it has none. */
function simplePaperFor(mode: ExportMode): string | null {
  const chips = mode.simplePaperIds;
  if (!chips || chips.length === 0) return null;
  return mode.simpleDefaultPaperId ?? chips[0] ?? null;
}

/**
 * Upper bound for a single rendered page, in megapixels.
 *
 * Every page — PDF pages included, since those embed a PNG — is rasterised
 * into one canvas of `mmToPx(mm, ratio)` (see `to-png.ts`). iPadOS/Safari caps
 * canvas area at roughly 16.7 MP; past that `canvas.toBlob` hands back null
 * and the export dies with "toBlob returned null". Large sheets at a high
 * pixel ratio blow through that easily: A2 at 3× is 32 MP, A1 at 2× is 28 MP.
 */
const MAX_PAGE_MEGAPIXELS = 16;

/** Rasterised size of one page at the given settings, in megapixels. */
function pageMegapixelsFor(
  paper: PaperSize,
  orientation: PaperOrientation,
  pixelRatio: number,
): number {
  const { wMm, hMm } = paperDimsMm(paper, orientation);
  return (mmToPx(wMm, pixelRatio) * mmToPx(hMm, pixelRatio)) / 1_000_000;
}

/**
 * Surface-agnostic export dialog.
 *
 * The dialog only controls **format meta-data** (paper, orientation, format,
 * pixel density, filename, mode, scale) — the actual list of pages is
 * computed by the caller via `buildPages`, so each surface (tactics,
 * whiteboard) chooses its own pagination strategy without the dialog
 * growing surface-specific branches.
 */
export function ExportDialog({
  title = "Exportieren",
  buildPages,
  getLayoutPreview,
  getAssembly,
  modes,
  defaultModeId,
  defaultFilename,
  defaultPaperId = "A4",
  defaultOrientation = "landscape",
  defaultFormat = "pdf",
  onConfirm,
  onClose,
}: ExportDialogProps) {
  const effectiveModes = modes && modes.length > 0 ? modes : [DEFAULT_MODE];
  const fallbackMode = effectiveModes[0] ?? DEFAULT_MODE;
  const initialMode = effectiveModes.find((m) => m.id === defaultModeId) ?? fallbackMode;
  const [modeId, setModeId] = useState(initialMode.id);
  const currentMode = effectiveModes.find((m) => m.id === modeId) ?? fallbackMode;

  // Poster modes open on their own paper (A2), not the dialog-wide default —
  // which stays whatever the non-poster modes want (A4).
  const [paperId, setPaperId] = useState(simplePaperFor(initialMode) ?? defaultPaperId);
  // Escape hatch out of a simple view into the full option list. Sticky for
  // the lifetime of the dialog so switching modes and back does not silently
  // fold the options away again.
  const [advanced, setAdvanced] = useState(false);
  // Posters larger than a home printer's sheet default to being tiled — that
  // is the only way most users can print them at all.
  const [tilePoster, setTilePoster] = useState(true);
  const [orientation, setOrientation] = useState<PaperOrientation>(defaultOrientation);
  const [format, setFormat] = useState<ExportFormat>(defaultFormat);
  const [pixelRatio, setPixelRatio] = useState(2);
  const [filename, setFilename] = useState(defaultFilename);
  const [scale, setScale] = useState(1);
  // Physical overlap (mm) shared between neighbouring tiles so printed sheets
  // can be glued together. Defaults to a sensible gluing margin.
  const [overlapMm, setOverlapMm] = useState(5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const paper = paperById(paperId) ?? PAPER_PRESETS[0]!;
  const dims = paperDimsMm(paper, orientation);

  // Warn before the export dies silently: large sheets at a high pixel ratio
  // exceed what Safari will hand back from `canvas.toBlob`.
  const pageMegapixels = pageMegapixelsFor(paper, orientation, pixelRatio);
  const oversizedPage = pageMegapixels > MAX_PAGE_MEGAPIXELS;

  const simpleChips = currentMode.simplePaperIds ?? null;
  // Tiling only makes sense while the poster is bigger than the print sheet.
  const canTile = currentMode.printSheetId !== undefined && paperId !== currentMode.printSheetId;
  /** Simple view = a poster-style mode, unless the user opened the options. */
  const simple = !advanced && simpleChips !== null && simpleChips.length > 0;
  const tiling = simple && canTile && tilePoster;

  /**
   * Switch modes, pulling the paper along when the target mode is a poster
   * mode whose chips do not include the current sheet — otherwise the simple
   * view would render with no chip selected.
   */
  const selectMode = (m: ExportMode) => {
    setModeId(m.id);
    const posterPaper = simplePaperFor(m);
    if (posterPaper && !m.simplePaperIds?.includes(paperId)) setPaperId(posterPaper);
  };

  // The simple view hides most controls, but their state stays alive behind
  // it. Feeding that state into the request would let a scale picked in the
  // advanced view leak into a poster export as a tiled grid, so the simple
  // view pins the values it does not ask about.
  const supportsScale = Boolean(currentMode.supportsScale);
  const posterOverlapMm = currentMode.posterOverlapMm ?? 5;
  const buildArgs: ExportDialogBuildArgs = useMemo(
    () => ({
      paper,
      orientation,
      modeId: currentMode.id,
      scale: simple ? 1 : supportsScale ? scale : 1,
      overlapMm: simple ? (tiling ? posterOverlapMm : 0) : supportsScale ? overlapMm : 0,
      tileOntoPrintSheet: tiling,
    }),
    [
      paper,
      orientation,
      currentMode.id,
      simple,
      supportsScale,
      scale,
      tiling,
      posterOverlapMm,
      overlapMm,
    ],
  );

  // Live preview of how many pages the surface plans to render.
  const pageCount = useMemo(() => {
    try {
      return buildPages(buildArgs).length;
    } catch {
      return 0;
    }
  }, [buildPages, buildArgs]);

  // Same call the submit path makes, so the info line can quote the finished
  // poster size before the user commits.
  const assemblyPreview = useMemo(() => {
    if (!tiling || !getAssembly) return null;
    try {
      return getAssembly(buildArgs);
    } catch {
      return null;
    }
  }, [getAssembly, tiling, buildArgs]);

  const layout = useMemo(() => {
    if (!getLayoutPreview) return null;
    try {
      return getLayoutPreview(buildArgs);
    } catch {
      return null;
    }
  }, [getLayoutPreview, buildArgs]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const pages = buildPages(buildArgs);
      if (pages.length === 0) {
        setError("Keine Seiten zum Exportieren.");
        setBusy(false);
        return;
      }
      const assembly = tiling ? (getAssembly?.(buildArgs) ?? null) : null;
      const request: ExportRequest = {
        pages,
        format,
        pixelRatio,
        filename: filename.trim() || defaultFilename,
        ...(assembly ? { assembly } : {}),
      };
      await onConfirm(request);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-200 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="glass-floating w-[min(480px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto rounded-3xl p-4 shadow-2xl">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-body-sm font-semibold uppercase tracking-wider">{title}</h2>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            aria-label="Schließen"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-white/6 hover:bg-white/10"
          >
            <X className="size-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy) void submit();
          }}
          className="flex flex-col gap-3"
        >
          {modes && modes.length > 1 && (
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Modus
              </span>
              <div className="flex h-10 overflow-hidden rounded-2xl border border-white/15 bg-white/4">
                {effectiveModes.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    disabled={busy}
                    onClick={() => selectMode(m)}
                    className={
                      "flex-1 text-body-sm transition-colors " +
                      (modeId === m.id ? "bg-pitch-lime/20 text-pitch-lime" : "hover:bg-white/10")
                    }
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </label>
          )}

          {simple && simpleChips && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Postergröße
                </span>
                <div className="flex gap-1.5">
                  {simpleChips.map((id) => {
                    const chipPaper = paperById(id);
                    if (!chipPaper) return null;
                    return (
                      <button
                        key={id}
                        type="button"
                        disabled={busy}
                        onClick={() => setPaperId(id)}
                        className={
                          "flex-1 rounded-2xl border px-3 py-2.5 text-body-sm transition-colors " +
                          (paperId === id
                            ? "border-pitch-lime/60 bg-pitch-lime/20 text-pitch-lime"
                            : "border-white/15 bg-white/4 hover:bg-white/10")
                        }
                      >
                        {chipPaper.id}
                      </button>
                    );
                  })}
                </div>
              </div>

              {canTile && (
                <label className="flex cursor-pointer items-start gap-2.5 rounded-2xl border border-white/10 bg-white/2 px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={tilePoster}
                    onChange={(e) => setTilePoster(e.target.checked)}
                    disabled={busy}
                    className="mt-0.5 size-4 shrink-0 accent-pitch-lime"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12px] font-semibold text-foreground">
                      Als zusammenklebbares Poster
                    </span>
                    <span className="block text-[11px] leading-snug text-muted-foreground">
                      Druckt auf {currentMode.printSheetId ?? "A4"}-Blättern, mit Schnittkanten und
                      einer Anleitung als letzter Seite.
                    </span>
                  </span>
                </label>
              )}

              <div className="rounded-2xl border border-white/10 bg-white/2 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
                {assemblyPreview ? (
                  <>
                    {pageCount} Blatt {currentMode.printSheetId ?? "A4"} + Anleitung · fertig{" "}
                    {Math.round(assemblyPreview.posterWMm)} ×{" "}
                    {Math.round(assemblyPreview.posterHMm)} mm
                  </>
                ) : (
                  <>
                    {Math.round(dims.wMm)} × {Math.round(dims.hMm)} mm · füllt das Blatt randlos
                  </>
                )}
              </div>
            </>
          )}

          {!simple && (
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Papierformat
                </span>
                <select
                  value={paperId}
                  onChange={(e) => setPaperId(e.target.value)}
                  disabled={busy}
                  className="h-10 rounded-2xl border border-white/15 bg-white/4 px-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:bg-white/7"
                >
                  {PAPER_PRESETS.map((p) => (
                    <option key={p.id} value={p.id} className="bg-[#101418]">
                      {p.id} ({p.widthMm}×{p.heightMm} mm)
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Ausrichtung
                </span>
                <div className="flex h-10 overflow-hidden rounded-2xl border border-white/15 bg-white/4">
                  {(["landscape", "portrait"] as const).map((o) => (
                    <button
                      key={o}
                      type="button"
                      disabled={busy}
                      onClick={() => setOrientation(o)}
                      className={
                        "flex-1 text-body-sm transition-colors " +
                        (orientation === o
                          ? "bg-pitch-lime/20 text-pitch-lime"
                          : "hover:bg-white/10")
                      }
                    >
                      {o === "landscape" ? "Quer" : "Hoch"}
                    </button>
                  ))}
                </div>
              </label>
            </div>
          )}

          {!simple && currentMode.supportsScale && (
            <label className="flex flex-col gap-1">
              <span className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>Skalierung</span>
                <span className="font-mono text-[11px] normal-case tracking-normal text-foreground">
                  {Math.round(scale * 100)}%
                </span>
              </span>
              <input
                type="range"
                min={0.25}
                max={4}
                step={0.05}
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
                disabled={busy}
                className="accent-pitch-lime"
              />
              <div className="mt-1 flex gap-1.5">
                {SCALE_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    disabled={busy}
                    onClick={() => setScale(p.value)}
                    className={
                      "flex-1 rounded-full px-2 py-1 text-[11px] transition-colors " +
                      (Math.abs(scale - p.value) < 0.01
                        ? "bg-pitch-lime/20 text-pitch-lime"
                        : "bg-white/4 hover:bg-white/10")
                    }
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </label>
          )}

          {!simple && currentMode.supportsScale && (
            <label className="flex flex-col gap-1">
              <span className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>Überlappung (Klebrand)</span>
                <span className="font-mono text-[11px] normal-case tracking-normal text-foreground">
                  {overlapMm} mm
                </span>
              </span>
              <div className="flex gap-1.5">
                {OVERLAP_PRESETS.map((mm) => (
                  <button
                    key={mm}
                    type="button"
                    disabled={busy}
                    onClick={() => setOverlapMm(mm)}
                    className={
                      "flex-1 rounded-full px-2 py-1 text-[11px] transition-colors " +
                      (overlapMm === mm
                        ? "bg-pitch-lime/20 text-pitch-lime"
                        : "bg-white/4 hover:bg-white/10")
                    }
                  >
                    {mm === 0 ? "Keine" : `${mm} mm`}
                  </button>
                ))}
              </div>
              <span className="text-[10px] leading-snug text-muted-foreground">
                Benachbarte Seiten teilen sich diesen Rand, damit sie sauber überlappend
                zusammengeklebt werden können.
              </span>
            </label>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Format
            </span>
            <div className="flex h-10 overflow-hidden rounded-2xl border border-white/15 bg-white/4">
              {(["pdf", "png"] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  disabled={busy}
                  onClick={() => setFormat(f)}
                  className={
                    "flex-1 text-body-sm uppercase tracking-wider transition-colors " +
                    (format === f ? "bg-pitch-lime/20 text-pitch-lime" : "hover:bg-white/10")
                  }
                >
                  {f}
                </button>
              ))}
            </div>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Auflösung
              </span>
              <select
                value={pixelRatio}
                onChange={(e) => setPixelRatio(Number(e.target.value))}
                disabled={busy}
                className="h-10 rounded-2xl border border-white/15 bg-white/4 px-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:bg-white/7"
              >
                <option value={1} className="bg-[#101418]">
                  Standard (1×)
                </option>
                <option value={2} className="bg-[#101418]">
                  Hoch (2×)
                </option>
                <option value={3} className="bg-[#101418]">
                  Druck (3×)
                </option>
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Dateiname
              </span>
              <input
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                disabled={busy}
                className="h-10 rounded-2xl border border-white/15 bg-white/4 px-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:bg-white/7"
              />
            </label>
          </div>

          {oversizedPage && (
            <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[11px] leading-snug text-amber-200">
              {Math.round(pageMegapixels)} Megapixel pro Seite. Auf iPad und Safari schlägt der
              Export oberhalb von rund {MAX_PAGE_MEGAPIXELS} MP fehl — bei Problemen eine Stufe
              niedriger wählen.
            </div>
          )}

          {!simple && layout && <LayoutPreview layout={layout} />}

          {!simple && (
            <div className="rounded-2xl border border-white/10 bg-white/2 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
              {pageCount} Seite{pageCount === 1 ? "" : "n"} · {Math.round(dims.wMm)}×
              {Math.round(dims.hMm)} mm ·{" "}
              {format === "pdf" ? "Mehrseitige PDF" : pageCount > 1 ? "Einzelne PNGs" : "PNG"}
            </div>
          )}

          {simpleChips && simpleChips.length > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setAdvanced((v) => !v)}
              className="self-start text-[11px] text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground disabled:opacity-50"
            >
              {simple ? "Erweiterte Optionen" : "Zurück zur einfachen Ansicht"}
            </button>
          )}

          {error && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-body-sm text-red-200">
              {error}
            </div>
          )}

          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => !busy && onClose()}
              disabled={busy}
              className="h-10 rounded-full border border-white/15 bg-white/4 px-4 text-body-sm hover:bg-white/10 disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy || pageCount === 0}
              className="h-10 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] shadow-glow hover:brightness-110 disabled:opacity-50"
            >
              {busy ? "Exportiere…" : "Exportieren"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Mini SVG preview: shows the page grid (one rect per sheet) with the
 * board overlaid in the exact position + size it will print at the current
 * scale. Letterbox margins, grid overlap, page seams — all to scale, in
 * one shared coordinate system (board units).
 */
function LayoutPreview({ layout }: { layout: ExportLayoutPreview }) {
  const { cols, rows, boardW, boardH, pageBoardW, pageBoardH } = layout;
  // Tiles advance by `step` (= page − overlap); with overlap they visibly
  // overlap in the preview. Falls back to the page size when no overlap.
  const stepW = layout.stepBoardW ?? pageBoardW;
  const stepH = layout.stepBoardH ?? pageBoardH;
  const overlapMm = layout.overlapMm ?? 0;
  const multi = cols * rows > 1;
  // Grid total (board units), centered around the board.
  const gridW = pageBoardW + (cols - 1) * stepW;
  const gridH = pageBoardH + (rows - 1) * stepH;
  // Bounding box of everything we draw = max of grid and board, anchored
  // at the board (0,0). Since the grid is centered on the board, the grid
  // origin starts at ((boardW-gridW)/2, (boardH-gridH)/2).
  const gridOriginX = (boardW - gridW) / 2;
  const gridOriginY = (boardH - gridH) / 2;
  const minX = Math.min(0, gridOriginX);
  const minY = Math.min(0, gridOriginY);
  const maxX = Math.max(boardW, gridOriginX + gridW);
  const maxY = Math.max(boardH, gridOriginY + gridH);
  const viewW = maxX - minX;
  const viewH = maxY - minY;

  // Render at ~200px max width / 130px max height with a small padding so
  // edge strokes don't get clipped at the SVG boundary.
  const pad = 4;
  const maxW = 200;
  const maxH = 130;
  const renderScale = Math.min((maxW - pad * 2) / viewW, (maxH - pad * 2) / viewH);
  const w = viewW * renderScale + pad * 2;
  const h = viewH * renderScale + pad * 2;
  const tx = (x: number) => pad + (x - minX) * renderScale;
  const ty = (y: number) => pad + (y - minY) * renderScale;

  // Board coverage on the page (single-page case) for the description.
  const onPageW = Math.min(boardW, pageBoardW);
  const onPageH = Math.min(boardH, pageBoardH);
  const coveragePct = Math.round(((onPageW * onPageH) / (pageBoardW * pageBoardH)) * 100);

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/2 px-3 py-2">
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0">
        {/* Page grid: one rect per sheet */}
        {Array.from({ length: cols * rows }).map((_, i) => {
          const c = i % cols;
          const r = Math.floor(i / cols);
          const x = gridOriginX + c * stepW;
          const y = gridOriginY + r * stepH;
          return (
            <rect
              key={`p-${i}`}
              x={tx(x)}
              y={ty(y)}
              width={pageBoardW * renderScale}
              height={pageBoardH * renderScale}
              fill="rgba(255,255,255,0.04)"
              stroke="rgba(255,255,255,0.55)"
              strokeWidth={1}
              strokeDasharray="3 2"
            />
          );
        })}
        {/* Board on top */}
        <rect
          x={tx(0)}
          y={ty(0)}
          width={boardW * renderScale}
          height={boardH * renderScale}
          fill="rgba(204, 255, 102, 0.28)"
          stroke="rgba(204, 255, 102, 0.85)"
          strokeWidth={1.5}
          rx={2}
        />
      </svg>
      <div className="flex flex-col gap-0.5 text-[11px] leading-tight">
        <span className="font-semibold text-foreground">
          {cols} × {rows} = {cols * rows} Seite{cols * rows === 1 ? "" : "n"}
        </span>
        <span className="text-muted-foreground">
          {cols * rows === 1
            ? `Feld nutzt ≈ ${coveragePct}% der Seite`
            : "Feld zentriert über alle Seiten"}
        </span>
        {multi && overlapMm > 0 && (
          <span className="text-muted-foreground">Überlappung {overlapMm} mm an den Nähten</span>
        )}
      </div>
    </div>
  );
}
